import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/types/graph';
import { AnimatedProfessionalAvatar } from '@/components/AnimatedProfessionalAvatar';
import { useQuestionnairePages } from '@/hooks/useQuestionnairePages';
import { useResetQuestionnaire } from '@/hooks/useResetQuestionnaire';
import { Button } from '@/components/ui/button';
import StarsIcon2 from '@/assets/Offboarding/StarsIcon2.png';
import { getMmgCheckInCopy } from '@/util/mmg';
import { isKioskOnboarding } from '@/util/kiosk';

// Placeholder professional that carries only the practice avatar; the empty
// name fields and MALE gender just select the default fallback avatar when the
// practice has none — mirrors the MMG landing page (LandingMmg).
const MALE_AVATAR_PLACEHOLDER = {
  firstname: '',
  lastname: '',
  title: '',
  avatarId: null,
  gender: 'MALE' as const,
};

/**
 * MMG (Maisons Médicales de Garde) check-in confirmation — the dedicated
 * offboarding page for both `mmg` (patient phone / QR) and `mmg_borne`
 * (waiting-room kiosk). It tells the patient they've been registered and joined
 * the on-call queue, gendered to their sex. The kiosk variant adds a "Nouveau
 * patient" reset so the next person can check in; the phone variant simply
 * invites the patient to close the window (ALDO-2540).
 */
export const MmgConfirmation = () => {
  const navigate = useNavigate();
  const resetQuestionnaire = useResetQuestionnaire();
  const [fadeIn, setFadeIn] = useState(false);
  // MMG check-in is a practice-level flow (often no individual professional is
  // selected), so the confirmation shows the practice avatar rather than a
  // professional's — consistent with the MMG landing page.
  const practice = useSelector(
    (state: RootState) => state.consultation.practice,
  );
  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );
  // Only the borne (mmg_borne kiosk) shows the "Nouveau patient" reset, since
  // it's a shared device that needs to return to the start for the next walk-in.
  const isKiosk = useSelector((state: RootState) =>
    isKioskOnboarding(state.questionnaire.config?.patientOnboarding),
  );
  const patientSex = useSelector(
    (state: RootState) => state.consultation.patient?.sex,
  );
  // An MMG invitation only ever comes from the kiosk continuation SMS (ALDO-2759)
  const isKioskContinuation = useSelector(
    (state: RootState) => state.graph.mode === 'INVITATION',
  );
  const mmgCopy = getMmgCheckInCopy(patientSex, isKioskContinuation);

  const { isLastPage } = useQuestionnairePages();

  useEffect(() => {
    if (!isLastPage) navigate('/questionnaire');
  }, [currentPage, isLastPage, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFadeIn(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`w-full h-full flex flex-col items-center justify-center text-base transition-opacity duration-600 ease-out px-4 n2xs:px-6 nsm:px-0 ${
        fadeIn ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="flex flex-col items-center justify-center h-full sm:w-full overflow-y-auto no-scrollbar">
        <div className="w-full max-w-md flex flex-col items-center text-center justify-between ">
          <div className="grow flex flex-col justify-center gap-2">
            <div
              className={`self-center flex flex-col shrink items-center justify-center z-10 transition-all duration-500 ease-out ${
                fadeIn ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
              }`}
            >
              <AnimatedProfessionalAvatar
                professional={{
                  ...MALE_AVATAR_PLACEHOLDER,
                  avatarId: practice?.avatarId ?? null,
                }}
                size={160}
              />
            </div>
            <div
              className={`flex flex-col justify-start gap-4 mt-6 transition-all duration-500 ease-out delay-300 ${
                fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <p className="text-xl font-medium px-10 mb-4">{mmgCopy.title}</p>
            </div>

            <div
              className={`flex items-center gap-4 p-6 mx-6 rounded-lg transition-all duration-500 ease-out delay-[600ms] bg-[#DAF6F3] ${
                fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <img src={StarsIcon2} alt="StarsIcon2" className="size-6" />
              <p className="font-light text-start leading-5">
                {mmgCopy.transmitted && (
                  <>
                    {mmgCopy.transmitted}
                    <br />
                  </>
                )}
                {mmgCopy.queue}
              </p>
            </div>

            {isKiosk ? (
              <div
                className={`mt-8 flex justify-center transition-all duration-500 ease-out delay-[900ms] ${
                  fadeIn
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
                }`}
              >
                <Button
                  variant="ctaPrimary"
                  size="lg"
                  onClick={() => resetQuestionnaire()}
                >
                  Nouveau patient
                </Button>
              </div>
            ) : (
              <p className="mt-10 text-indigo-900 font-light">
                Vous pouvez fermer la fenêtre
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
